import { useState, useRef, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { User, Settings, LogOut, Sun, Moon, Check } from 'lucide-react';
import { signOutUser, useAuth } from '@weebee/api';
import { useCompany } from '@/lib/company-context';

export function UserMenu() {
  const authState = useAuth();
  const { company, level, memberships, switchCompany } = useCompany();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function toggleTheme() {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  }

  const displayName =
    authState.status === 'authenticated'
      ? (authState.user.displayName ?? authState.user.phoneNumber ?? 'User')
      : 'User';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        aria-label="User menu"
      >
        <User className="h-4 w-4" />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1 w-64 rounded-lg border border-border bg-card shadow-lg py-1 animate-scale-in z-50">
          {/* User info + company context */}
          <div className="px-3 py-2 border-b border-border">
            <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
            <p className="text-xs text-muted-foreground truncate">{company.name}</p>
          </div>

          {/* Company switcher (multi-company users only) */}
          {memberships.length > 1 && (
            <div className="border-b border-border py-1">
              {memberships.map((m) => (
                <button
                  key={m.companySlug}
                  type="button"
                  onClick={() => {
                    switchCompany(m.companySlug);
                    setOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent transition-colors"
                >
                  <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center shrink-0">
                    {m.companyLogoUrl ? (
                      <img
                        src={m.companyLogoUrl}
                        alt=""
                        className="h-6 w-6 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-xs font-medium text-muted-foreground">
                        {m.companyName.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <span className="flex-1 text-sm text-foreground truncate">{m.companyName}</span>
                  {m.companySlug === company.slug && (
                    <Check className="h-4 w-4 text-primary shrink-0" />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Theme toggle */}
          <button
            type="button"
            onClick={toggleTheme}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            {dark ? 'Light mode' : 'Dark mode'}
          </button>

          {/* Settings */}
          {level >= 7 && (
            <Link
              to="/app/settings"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <Settings className="h-4 w-4" />
              Company Settings
            </Link>
          )}

          {/* Sign out */}
          <div className="border-t border-border mt-1 pt-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void signOutUser();
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
